import { useEffect, useState } from 'react';
import { Globe2, Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import { formatTimeInTimeZone } from '@/lib/date-utils';

const TIME_ZONES = ['UTC', ...Intl.supportedValuesOf('timeZone')];

function validTimeZone(value: string | undefined): value is string {
  if (!value) return false;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

function browserTimeZone(): string {
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return validTimeZone(detected) ? detected : 'UTC';
}

interface TimeZoneConfirmationStepProps {
  confirmationState: 'pending' | 'provisioned';
  savedTimeZone: string;
  onConfirm: (timeZone: string) => Promise<void>;
  isSaving: boolean;
}

export function TimeZoneConfirmationStep({
  confirmationState,
  savedTimeZone,
  onConfirm,
  isSaving,
}: TimeZoneConfirmationStepProps) {
  const [timeZone, setTimeZone] = useState(() => (
    confirmationState === 'provisioned' && validTimeZone(savedTimeZone)
      ? savedTimeZone
      : browserTimeZone()
  ));
  const [now, setNow] = useState(() => Date.now());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const handleConfirm = async () => {
    setError(null);
    try {
      await onConfirm(timeZone);
    } catch {
      setError('Failed to confirm time zone. Please try again.');
    }
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-8 sm:py-12">
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            <Globe2 className="h-6 w-6 text-blue-600" />
          </div>
          <CardTitle>Confirm your organization time zone</CardTitle>
          <CardDescription>
            Dates, times, and scheduled activity will use this zone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="onboarding-time-zone">Time zone</Label>
            <select
              id="onboarding-time-zone"
              value={timeZone}
              onChange={(event) => setTimeZone(event.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              {TIME_ZONES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-500">
              Current time in this zone: {formatTimeInTimeZone(new Date(now).toISOString(), timeZone)}
            </p>
          </div>
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <Button
            className="w-full"
            onClick={() => void handleConfirm()}
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirm time zone
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
